// frontend/app/components/lessonPlans/present/PresentSarasPanel.js
//
// "Let's see what Saras says" — on the projector, with the whole class reading
// the answer at once.
//
// This is the one place in present mode where something new arrives on the wall
// mid-lesson, and that changes what the surface has to do. Everywhere else the
// content is authored and instant; here thirty people are watching a request
// that takes seconds. So the question the teacher asked stays on screen at
// prompt size for the entire wait — the room always has something to read — and
// the thinking state is stated in words rather than left to a spinner nobody at
// the back can see. Answer text streams in as it arrives, which turns most of
// the wait into reading time.
//
// Failure is a teaching moment, not a stack trace. When the ask fails the panel
// says something a teacher can read out and carry on from; a raw error message
// on a wall in front of a class is the worst possible version of this feature.
//
// A finished answer can also be read out loud — see useReadAloud for why that
// is the browser's own voice rather than the app's TTS, and the note beside the
// hook call below for why the button waits for the stream to finish. The voice
// stops whenever the answer stops being the thing on the wall: a new question,
// going back to the question list, the panel closing, the next step.
//
// An authored asset summary arrives here too, through `initialAnswer`, and is
// drawn by the same code that draws a streamed answer — a class must not be
// able to tell the two apart. That path makes NO network call, and the exchange
// it opens is closed: no follow-up, because Saras has not seen those words.
//
// IT RECORDS NOTHING. Asking Saras is not a student answer — there are no
// student devices in the room, and nothing here is attributed to anyone. The
// panel takes no onAnswer, no onCheck and no session state, and the only
// network call it can make is the one in presentAskClient. Reading an answer
// aloud adds no second one: it speaks a string the panel already holds and
// reaches nothing outside the browser.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { PRESENT_ASK_MAX_CHARS, askSaras as defaultAskSaras } from "./presentAskClient";
import { ChromeButton } from "./PresentSurface";
import useReadAloud, { primeReadAloud } from "./useReadAloud";
import useOpenAiChunkedSpeech from "./useOpenAiChunkedSpeech";
import { isSwanHelpRequest } from "../../journeys/journeySwanResponseGuard";

/** idle → asking → answered | failed. Nothing else, and no partial states. */
const IDLE = "idle";
const ASKING = "asking";
const ANSWERED = "answered";
const FAILED = "failed";

/** One authored question, sized to be tapped without looking at the screen. */
const QuestionRow = ({
  question,
  index,
  colors,
  type,
  onPress,
  compact = false,
}) => (
  <TouchableOpacity
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={question}
    style={[
      styles.questionRow,
      compact ? styles.swanQuestionRow : null,
      { borderColor: colors.aiCardBorder, backgroundColor: colors.aiCardBg },
    ]}
  >
    <Text
      style={[
        styles.questionIndex,
        type.caption,
        compact ? styles.swanQuestionIndex : null,
        { color: colors.accentPrimary },
      ]}
    >
      {index + 1}
    </Text>
    <Text style={[
        styles.questionText,
        type.option,
        compact ? styles.swanQuestionText : null,
        { color: colors.textPrimary },
      ]}>
      {question}
    </Text>
    <MaterialCommunityIcons
      name="chevron-right"
      size={compact ? 20 : Math.round(type.option.fontSize * 1.2)}
      color={colors.accentPrimary}
    />
  </TouchableOpacity>
);

const PresentSarasPanel = ({
  // The step's authored ai_prompt questions, in authored order. May be empty —
  // a class asks its own questions on steps that never planned for one.
  questions = [],
  colors,
  type,
  onClose,
  // Injected so the caller can supply the app's own ask; defaults to present
  // mode's own client so the panel works with nothing wired up.
  askSaras = defaultAskSaras,
  userId,
  language = "en",
  // Set when the rail was opened BY pressing a question on the slide. It is
  // asked once, on open — the teacher pressed it, so making them press again
  // inside the rail would be asking the same question twice.
  initialQuestion = null,
  // The answer, already written, for the openings that are not questions at
  // all: an asset's authored summary. Paired with `initialQuestion`, which is
  // then the heading over it rather than something to send anywhere.
  //
  // WHY THIS IS NOT JUST AN ASK WITH A GOOD PROMPT. The author wrote these
  // words for this moment. They arrive instantly, in front of a class that does
  // not wait well; they are the same words in period 4 that were on the wall in
  // period 2, which matters the moment a teacher says "as we saw earlier"; and
  // they cost nothing and cannot fail. Nothing about a live ask can promise
  // any of that, and none of it is worth trading for novelty in a summary of a
  // scene that has not changed.
  //
  // It reaches no network. `askSaras` is not called on this path, so present
  // mode's "records nothing" guarantee holds here by construction rather than
  // by care — there is no request to record anything.
  initialAnswer = null,
  assistantName = "Saras",
  conversationMode = false,
  // The current authored activity: a new gate gets a new sequence of hints.
  coachingScope = null,
  // In unified self-study mode Swan's avatar/Back button are provided by the
  // enclosing rail. This component remains the ONE conversation engine.
  embedded = false,
  focusedConversation = false,
  active = true,
  autoReadDefault = false,
  autoReadEnabled: controlledAutoReadEnabled,
  onAutoReadEnabledChange,
  onReadAloudSpeakingChange,
  onThinkingChange,
  streamAudioAllowed = true,
  t,
}) => {
  const translate = t || ((value) => value);
  const list = Array.isArray(questions) ? questions.filter(Boolean) : [];
  const assistant = String(assistantName || "Saras").trim() || "Saras";

  const [status, setStatus] = useState(IDLE);
  const [asked, setAsked] = useState("");
  const [answer, setAnswer] = useState("");
  const [draft, setDraft] = useState("");
  // In embedded child mode the conversation is for reading first. The follow-up
  // composer is revealed on demand, so it cannot steal the answer viewport.
  const [followUpExpanded, setFollowUpExpanded] = useState(false);
  // True while the answer on screen is authored rather than answered. It does
  // not change how the answer is DRAWN — the whole point is that a class cannot
  // tell the two apart — only what the teacher is offered next to it.
  const [authored, setAuthored] = useState(false);
  const [internalAutoReadEnabled, setInternalAutoReadEnabled] =
    useState(Boolean(autoReadDefault));
  const autoReadEnabled =
    typeof controlledAutoReadEnabled === "boolean"
      ? controlledAutoReadEnabled
      : internalAutoReadEnabled;
  const setAutoReadEnabled = (nextValue) => {
    const resolved =
      typeof nextValue === "function"
        ? nextValue(autoReadEnabled)
        : nextValue;
    if (typeof controlledAutoReadEnabled !== "boolean") {
      setInternalAutoReadEnabled(resolved);
    }
    onAutoReadEnabledChange?.(resolved);
  };
  const lastAutoReadRef = useRef("");
  // Only ephemeral conversation hints; never changes Journey completion.
  const helpRef = useRef({ scope: null, attempts: 0, lastAnswer: "" });

  const abortRef = useRef(null);
  // The first ask of a session starts its own thread: the teacher's laptop may
  // have a conversation on it from before the lesson, and the room's question
  // should not be read as a follow-up to that. Later asks in the same panel do
  // follow on, because "and why is that?" is exactly what a class says next.
  const startedRef = useRef(false);
  const mountedRef = useRef(true);

  // Unguarded Ask AI responses use low-latency streamed audio chunks.
  // Guarded responses and phase reviews must finish their reveal checks
  // before their final text goes to the existing OpenAI TTS synthesizer.
  // Teacher-led manual Read Aloud now uses the same voice on demand.
  const readAloud = useReadAloud({ language });
  const openAiSpeech = useOpenAiChunkedSpeech({ userId, language });
  const { stop: stopBrowserReading } = readAloud;
  const stopReading = useCallback(() => {
    stopBrowserReading();
    openAiSpeech.stop();
  }, [stopBrowserReading, openAiSpeech.stop]);
  // Authored messages and protected replies are spoken only when the final
  // visible text is available; ordinary replies retain incremental playback.
  const liveOpenAiSpeech = conversationMode && autoReadEnabled && streamAudioAllowed && !authored;
  const liveSpeechEnabledRef = useRef(liveOpenAiSpeech);
  liveSpeechEnabledRef.current = liveOpenAiSpeech;
  useEffect(() => {
    if (!autoReadEnabled && conversationMode) openAiSpeech.stop();
  }, [autoReadEnabled, conversationMode, openAiSpeech.stop]);

  // The child can return to the activity without losing this conversation.
  // Stop a pending reply/voice while the chat is hidden; keep the displayed
  // last exchange intact for reopening in the same phase.
  useEffect(() => {
    if (!embedded || active) return;
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    // Reopening after an aborted request must not display a stuck spinner.
    setStatus((current) => current === ASKING ? IDLE : current);
    stopReading();
    onReadAloudSpeakingChange?.(false);
  }, [active, embedded, onReadAloudSpeakingChange, stopReading]);

  useEffect(() => {
    onThinkingChange?.(active && status === ASKING);
    return () => onThinkingChange?.(false);
  }, [active, onThinkingChange, status]);

  useEffect(() => {
    onReadAloudSpeakingChange?.(Boolean(openAiSpeech.speaking));
    return () => onReadAloudSpeakingChange?.(false);
  }, [onReadAloudSpeakingChange, openAiSpeech.speaking]);

  useEffect(() => {
    if (!conversationMode || !autoReadEnabled || (liveOpenAiSpeech && !authored)) return;
    if (status !== ANSWERED) return;
    const text = String(answer || "").trim();
    if (!text) return;
    const key = `${asked}\n${text}`;
    if (lastAutoReadRef.current === key) return;
    lastAutoReadRef.current = key;
    // Guarded/generated reviews AND fixed text are spoken only after approval.
    // This is the same OpenAI TTS model/voice as streaming Ask AI chunks.
    openAiSpeech.speakText(text);
  }, [answer, asked, autoReadEnabled, authored, conversationMode, liveOpenAiSpeech, openAiSpeech.speakText, status]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // A teacher who closed the panel is not waiting for this any more.
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const ask = useCallback(
    async (question) => {
      const text = String(question || "").trim();
      if (!text) return;

      // The last answer is being replaced on the wall, so it must stop being
      // read off it. Before the request rather than after, so the room never
      // hears the old answer over the new question.
      stopReading();
      // Keep speech enabled through this explicit child tap. Chrome may reject
      // the later read-aloud call after the network request loses user gesture.
      // Guarded speech will be synthesized from the approved final text.


      if (abortRef.current) abortRef.current.abort();
      const controller =
        typeof AbortController === "function" ? new AbortController() : null;
      abortRef.current = controller;

      const newDialog = !startedRef.current;
      startedRef.current = true;
      if (conversationMode && helpRef.current.scope !== coachingScope) {
        helpRef.current = { scope: coachingScope, attempts: 0, lastAnswer: "" };
      }
      if (conversationMode && coachingScope && isSwanHelpRequest(text)) {
        helpRef.current.attempts += 1;
      }
      const hintAttempt = conversationMode ? helpRef.current.attempts : 0;
      const previousAnswer = conversationMode ? helpRef.current.lastAnswer : "";

      setAsked(text);
      setAnswer("");
      setAuthored(false);
      lastAutoReadRef.current = "";
      setStatus(ASKING);

      try {
        const result = await askSaras({
          question: text,
          userId,
          language,
          newDialog,
          hintAttempt,
          previousAnswer,
          signal: controller?.signal,
          // The client calls the existing /api/ask_ai stream, not ask_ai_stream.
          // Do not play a raw unguarded model answer when the calling Journey
          // applies an inquiry/reveal guard after generation.
          audioEnabled: conversationMode && autoReadEnabled && streamAudioAllowed,
          onAudioChunk: (url) => {
            if (liveSpeechEnabledRef.current) openAiSpeech.enqueue(url);
          },
          onDelta: (partial) => {
            // Streaming is what makes the wait bearable: the first sentence is
            // usually on the wall long before the answer is finished.
            if (!mountedRef.current) return;
            if (abortRef.current !== controller) return;
            setAnswer(partial);
          },
        });

        if (!mountedRef.current) return;
        if (abortRef.current !== controller) return;
        // A null result is a cancelled request, which has nothing to draw.
        if (result === null) return;

        if (conversationMode) helpRef.current.lastAnswer = String(result || "");
        setAnswer(result);
        setStatus(ANSWERED);
      } catch (_error) {
        if (!mountedRef.current) return;
        if (abortRef.current !== controller) return;
        // Deliberately no detail from the thrown error. Whatever it says, it
        // was written for a developer, and this is a wall in front of a class.
        setStatus(FAILED);
      }
    },
    [askSaras, autoReadEnabled, coachingScope, conversationMode, language, openAiSpeech.enqueue, stopReading, streamAudioAllowed, userId]
  );

  /** Put authored text on the wall as the answer. No request is made. */
  const show = useCallback(
    (question, text) => {
      // The panel is being repainted, so anything it was doing stops: a voice
      // reading the last answer, and any ask still in flight behind it.
      stopReading();
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setAsked(question);
      setAnswer(text);
      setAuthored(true);
      setStatus(ANSWERED);
      // `startedRef` is deliberately left alone, and that is what keeps the
      // model honest. Nothing was said to Saras here, so the next real ask in
      // this panel is still the FIRST one and still opens its own thread —
      // Saras is never carrying a turn it did not produce, and can never be
      // asked to follow up on words it has not seen.
    },
    [stopReading]
  );

  // Fire the opening once. Keyed on the question AND the answer, not the
  // question alone: two 3D scenes on one slide carry the SAME label — "What is
  // this Play With Object (PWO)?" — and keying on that string would make the
  // second press a no-op showing the first scene's summary. Re-renders in
  // between still do not re-fire.
  const openedWithRef = useRef({ question: null, answer: null });
  useEffect(() => {
    const text = String(initialQuestion || "").trim();
    if (!text) return;

    const authored = String(initialAnswer || "").trim();
    const seen = openedWithRef.current;
    if (seen.question === text && seen.answer === authored) return;
    openedWithRef.current = { question: text, answer: authored };

    if (authored) show(text, authored);
    else ask(text);
  }, [ask, show, initialQuestion, initialAnswer]);

  const submitDraft = useCallback(() => {
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    setFollowUpExpanded(false);
    ask(text);
  }, [ask, draft]);

  const backToQuestions = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    // The answer is coming off the wall; the voice reading it goes with it.
    stopReading();
    setStatus(IDLE);
    setAsked("");
    setAnswer("");
    setAuthored(false);
  }, [stopReading]);

  const asking = status === ASKING;
  const showingResult = status !== IDLE;

  // Only over a finished answer, and only where there is an engine to read it:
  // where `speechSynthesis` is missing this renders nothing at all rather than
  // a control that would do nothing when a teacher pressed it.
  const canReadAloud =
    openAiSpeech.supported && status === ANSWERED && !!answer.trim();

  return (
    <View
      testID="present-saras-panel"
      style={[
        styles.panel,
        embedded ? styles.embeddedSwanPanel : null,
        focusedConversation ? styles.focusedSwanPanel : null,
        { backgroundColor: colors.panelBg, borderColor: colors.aiCardBorder },
      ]}
    >
      {!embedded ? <View
        style={[
          styles.header,
          conversationMode ? styles.swanHeader : null,
          { borderBottomColor: colors.border },
        ]}
      >
        <MaterialCommunityIcons
          name="robot-happy-outline"
          size={conversationMode ? 24 : Math.round(type.stepTitle.fontSize * 0.9)}
          color={colors.accentPrimary}
        />
        <Text
          numberOfLines={conversationMode ? 1 : undefined}
          style={[
            styles.headerText,
            type.eyebrow,
            conversationMode ? styles.swanHeaderText : null,
            { color: colors.accentPrimary },
          ]}
        >
          {translate(`Ask ${assistant}`)}
        </Text>
        {!conversationMode ? (
          <Text style={[styles.headerHint, type.chrome, { color: colors.textMuted }]}>
            {translate("Esc or S to close")}
          </Text>
        ) : null}
        <ChromeButton
          colors={colors}
          type={type}
          icon="close"
          label={translate("Close")}
          onPress={onClose}
        />
      </View> : null}

      {showingResult ? (
        <View
          style={[
            styles.resultWrap,
            conversationMode ? styles.swanResultWrap : null,
            embedded ? styles.embeddedSwanContent : null,
          ]}
        >
          {/* The question stays put through the wait and through the answer.
              Thirty people looking at a blank panel is the failure this
              prevents, and once the answer lands the room still needs to see
              what it is an answer to. */}
          {!conversationMode ? <Text style={[
            styles.askedText,
            type.prompt,
            { color: colors.textPrimary },
          ]}>
            {asked}
          </Text> : null}

          {asking ? (
            <View style={styles.thinkingRow}>
              <ActivityIndicator size="small" color={colors.accentPrimary} />
              <Text
                style={[
                  styles.thinkingText,
                  type.option,
                  conversationMode ? styles.swanAnswerText : null,
                  { color: colors.accentPrimary },
                ]}
              >
                {translate(`${assistant} is thinking…`)}
              </Text>
            </View>
          ) : null}

          {status === FAILED ? (
            <View
              style={[
                styles.failedPanel,
                {
                  backgroundColor: colors.teacherTipBg,
                  borderColor: colors.teacherTipBorder,
                },
              ]}
            >
              <Text
                style={[
                  styles.failedText,
                  type.body,
                  conversationMode ? styles.swanAnswerText : null,
                  { color: colors.textPrimary },
                ]}
              >
                {translate(
                  conversationMode
                    ? `${assistant} could not answer that one just now. Try again, or tell ${assistant} what you observed in your own words.`
                    : "Saras could not answer that one just now. Let's take it ourselves and come back to it."
                )}
              </Text>
            </View>
          ) : (
            // The same scroller serves the partial text and the finished
            // answer, so nothing jumps when the last token arrives. A long
            // answer scrolls in here; the slide behind it never moves.
            <ScrollView
              style={styles.answerScroll}
              contentContainerStyle={styles.answerContent}
              showsVerticalScrollIndicator
              nestedScrollEnabled
              accessibilityLabel={translate("Professor Swan full answer. Scroll to read more.")}
            >
              {conversationMode ? (
                <Text style={[styles.swanAskedText, { fontWeight: "800", color: colors.textPrimary }]}>
                  {asked}
                </Text>
              ) : null}
              {answer ? (
                <Text
                  style={[
                    styles.answerText,
                    type.body,
                    conversationMode ? styles.swanAnswerText : null,
                    { color: colors.textPrimary },
                  ]}
                >
                  {answer}
                </Text>
              ) : null}
            </ScrollView>
          )}

          {conversationMode && !authored ? (
            <View style={styles.conversationFollowUp}>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityState={{ expanded: followUpExpanded }}
                accessibilityLabel={translate(followUpExpanded ? "Hide follow-up input" : "Reply to Professor Swan or ask another question")}
                onPress={() => setFollowUpExpanded((current) => !current)}
                style={styles.swanFollowUpToggle}
              >
                <Text style={[styles.swanFollowUpLabel, { color: colors.accentPrimary }]}>
                  {translate(followUpExpanded ? "Hide reply ⌃" : "Reply or ask a follow-up ⌄")}
                </Text>
              </TouchableOpacity>
              {followUpExpanded ? <View
                style={[
                  styles.conversationAskBox,
                  styles.swanConversationAskBox,
                  { borderColor: colors.border },
                ]}
              >
                <TextInput
                  value={draft}
                  onChangeText={setDraft}
                  maxLength={PRESENT_ASK_MAX_CHARS}
                  multiline
                  textAlignVertical="top"
                  accessibilityLabel={translate(`Reply to ${assistant}`)}
                  placeholder={translate(`Type your answer to ${assistant}…`)}
                  placeholderTextColor={colors.textMuted}
                  style={[
                    styles.conversationAskInput,
                    styles.swanConversationAskInput,
                    {
                      color: colors.textPrimary,
                      fontSize: 16,
                      lineHeight: 24,
                    },
                  ]}
                />
                <View style={styles.conversationSendRow}>
                  <ChromeButton
                    colors={colors}
                    type={type}
                    icon="send"
                    label={translate("Send")}
                    active
                    accent={colors.accentPrimary}
                    disabled={!draft.trim()}
                    onPress={submitDraft}
                  />
                </View>
              </View> : null}
            </View>
          ) : null}

          <View
            style={[styles.resultActions, conversationMode ? styles.swanResultActions : null]}
          >
            {canReadAloud ? (
              conversationMode ? (
                <ChromeButton
                  colors={colors}
                  type={type}
                  icon={autoReadEnabled ? "volume-high" : "volume-off"}
                  label={translate(autoReadEnabled ? "Read aloud: On" : "Read aloud: Off")}
                  active={autoReadEnabled}
                  accent={autoReadEnabled ? colors.accentPrimary : undefined}
                  onPress={() => {
                    if (autoReadEnabled) stopReading();
                    lastAutoReadRef.current = "";
                    setAutoReadEnabled((value) => !value);
                  }}
                />
              ) : (
                <ChromeButton
                  colors={colors}
                  type={type}
                  icon={openAiSpeech.speaking ? "stop" : "volume-high"}
                  label={openAiSpeech.phase === "preparing"
                    ? translate("Preparing audio…")
                    : openAiSpeech.speaking ? translate("Stop audio") : translate("Listen to answer")}
                  active={openAiSpeech.speaking}
                  accent={openAiSpeech.speaking ? colors.accentPrimary : undefined}
                  onPress={() => openAiSpeech.speaking
                    ? openAiSpeech.stop()
                    : openAiSpeech.speakText(answer, { fromUserGesture: true })}
                />
              )
            ) : null}
            {status === FAILED ? (
              <ChromeButton colors={colors} type={type} icon="refresh" label={translate("Try again")} active accent={colors.accentPrimary} onPress={() => ask(asked)} />
            ) : null}
            {(!conversationMode || authored) ? (
              <ChromeButton colors={colors} type={type} icon="chevron-left" label={authored ? translate(`Ask ${assistant} a question`) : translate("Ask something else")} onPress={backToQuestions} />
            ) : null}
          </View>
          {!conversationMode && openAiSpeech.error ? (
            <Text style={[type.caption, { color: colors.textPrimary, marginTop: 8 }]} accessibilityRole="alert">
              {translate(openAiSpeech.error)}
            </Text>
          ) : null}
        </View>
      ) : (
        <View style={[
          styles.idleWrap,
          conversationMode ? styles.swanIdleWrap : null,
          embedded ? styles.embeddedSwanContent : null,
        ]}>
          <Text style={[
            styles.idleLabel,
            type.eyebrow,
            conversationMode ? styles.swanIdleLabel : null,
            { color: colors.textMuted },
          ]}>
            {conversationMode
              ? list.length > 0
                ? translate(`Tap a question, or ask ${assistant} in your own words`)
                : translate(`Ask ${assistant} a question`)
              : list.length > 0
                ? translate("Tap a question, or type one the class just asked")
                : translate("Type the question the class just asked")}
          </Text>

          {list.length > 0 ? (
            <ScrollView
              style={styles.questionScroll}
              contentContainerStyle={styles.questionContent}
              showsVerticalScrollIndicator={false}
            >
              {list.map((question, index) => (
                <QuestionRow
                  key={`saras-q-${index}`}
                  question={question}
                  index={index}
                  colors={colors}
                  type={type}
                  compact={conversationMode}
                  onPress={() => ask(question)}
                />
              ))}
            </ScrollView>
          ) : (
            <View style={styles.questionSpacer} />
          )}

          {conversationMode ? (
            <View
              style={[
                styles.conversationAskBox,
                styles.swanConversationAskBox,
                { borderColor: colors.border },
              ]}
            >
              <TextInput
                value={draft}
                onChangeText={setDraft}
                maxLength={PRESENT_ASK_MAX_CHARS}
                multiline
                textAlignVertical="top"
                accessibilityLabel={translate(`Ask ${assistant}`)}
                placeholder={translate(`Ask ${assistant} a question…`)}
                placeholderTextColor={colors.textMuted}
                style={[
                  styles.conversationAskInput,
                  styles.swanConversationAskInput,
                  {
                    color: colors.textPrimary,
                    fontSize: 16,
                    lineHeight: 24,
                  },
                ]}
              />
              <View style={styles.conversationSendRow}>
                <ChromeButton
                  colors={colors}
                  type={type}
                  icon="send"
                  label={translate("Ask")}
                  active
                  accent={colors.accentPrimary}
                  disabled={!draft.trim()}
                  onPress={submitDraft}
                />
              </View>
            </View>
          ) : (
            <View style={[styles.askRow, { borderColor: colors.border }]}>
              <TextInput
                value={draft}
                onChangeText={setDraft}
                onSubmitEditing={submitDraft}
                maxLength={PRESENT_ASK_MAX_CHARS}
                returnKeyType="send"
                blurOnSubmit={false}
                accessibilityLabel={translate(`Ask ${assistant}`)}
                placeholder={translate(`Ask ${assistant} a question…`)}
                placeholderTextColor={colors.textMuted}
                style={[
                  styles.askInput,
                  {
                    color: colors.textPrimary,
                    fontSize: type.option.fontSize,
                    lineHeight: type.option.lineHeight,
                  },
                ]}
              />
              <ChromeButton
                colors={colors}
                type={type}
                icon="send"
                label={translate("Ask")}
                active
                accent={colors.accentPrimary}
                disabled={!draft.trim()}
                onPress={submitDraft}
              />
            </View>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  focusedSwanPanel: {
    // Reading-first workspace: a bounded line length and full height for the
    // conversation, instead of a 344px column competing with the activity.
    width: "100%", maxWidth: 860, alignSelf: "center", flex: 1, minHeight: 0,
  },
  embeddedSwanPanel: {
    borderWidth: 0,
    borderRadius: 12,
    maxWidth: undefined,
  },
  embeddedSwanContent: {
    padding: 8,
    minHeight: 0,
  },
  panel: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    maxWidth: 1180,
    alignSelf: "center",
    borderRadius: 20,
    borderWidth: 2,
    overflow: "hidden",
  },

  header: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    paddingHorizontal: 24,
    paddingVertical: 14,
    gap: 12,
  },
  headerText: {
    fontWeight: "800",
    letterSpacing: 1.2,
    textTransform: "uppercase",
    marginLeft: 10,
  },
  headerHint: { fontWeight: "600", flex: 1, textAlign: "right", marginRight: 4 },

  // Student-side Professor Swan is much narrower than the classroom projector.
  // Keep these overrides inside conversationMode so teacher-present typography
  // and controls retain their original large-screen behavior.
  swanHeader: { paddingHorizontal: 16, paddingVertical: 12, gap: 10 },
  swanHeaderText: {
    flex: 1,
    minWidth: 0,
    marginLeft: 0,
    fontSize: 15,
    lineHeight: 21,
    letterSpacing: 0.5,
  },
  swanResultWrap: { flex: 1, padding: 7, minHeight: 0 },
  swanAskedText: { fontSize: 16, lineHeight: 21, marginBottom: 4 },
  swanAnswerText: { fontSize: 17, lineHeight: 26 },
  swanFollowUpLabel: { fontSize: 13, lineHeight: 19, letterSpacing: 0 },
  swanConversationAskBox: { borderRadius: 12, padding: 5, minHeight: 48 },
  swanConversationAskInput: { minHeight: 24, maxHeight: 54 },
  // The unified child rail is only 344px wide. Let the phase-review actions
  // wrap instead of hiding the second button beyond the clipped rail.
  // Leave teacher-led / classroom resultActions layout unchanged.
  swanResultActions: {
    marginTop: 6,
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: 8,
  },
  swanFollowUpToggle: { paddingVertical: 8, minHeight: 36, justifyContent: "center" },
  swanIdleWrap: { padding: 18 },
  swanIdleLabel: { fontSize: 13, lineHeight: 19, marginBottom: 12 },
  swanQuestionRow: {
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 8,
  },
  swanQuestionIndex: { fontSize: 13, lineHeight: 20, marginRight: 10 },
  swanQuestionText: { fontSize: 16, lineHeight: 24, marginRight: 8 },

  idleWrap: { flex: 1, minHeight: 0, padding: 24 },
  idleLabel: {
    fontWeight: "800",
    letterSpacing: 1.1,
    textTransform: "uppercase",
    marginBottom: 18,
  },
  questionScroll: { flex: 1, minHeight: 0 },
  questionContent: { paddingBottom: 4 },
  questionSpacer: { flex: 1 },
  questionRow: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 14,
    borderWidth: 2,
    paddingHorizontal: 20,
    paddingVertical: 16,
    marginBottom: 12,
  },
  questionIndex: { fontWeight: "800", marginRight: 16 },
  questionText: { flex: 1, fontWeight: "600", marginRight: 12 },

  askRow: {
    flexDirection: "row",
    alignItems: "center",
    borderTopWidth: 1,
    paddingTop: 18,
    marginTop: 18,
    gap: 12,
  },
  askInput: {
    flex: 1,
    paddingVertical: 8,
    // No border of its own: the row's rule already separates it, and an outlined
    // box on a projector reads as a form to fill in.
    fontWeight: "500",
  },

  resultWrap: { flex: 1, minHeight: 0, padding: 24 },
  askedText: { fontWeight: "700", marginBottom: 18 },

  thinkingRow: { flexDirection: "row", alignItems: "center", marginBottom: 16 },
  thinkingText: { fontWeight: "800", marginLeft: 14 },

  // Text stays in this real scroll region; never limit answer to a number of lines.
  answerScroll: { flex: 1, minHeight: 0 },
  answerContent: { paddingBottom: 8 },
  answerText: { fontWeight: "400" },

  failedPanel: {
    borderRadius: 16,
    borderWidth: 2,
    padding: 22,
    marginBottom: 4,
  },
  failedText: { fontWeight: "600" },

  conversationFollowUp: {
    marginTop: 6,
    gap: 5,
  },
  followUpLabel: {
    fontWeight: "800",
  },
  conversationAskBox: {
    borderWidth: 1,
    borderRadius: 16,
    padding: 12,
    minHeight: 128,
  },
  conversationAskInput: {
    minHeight: 76,
    paddingHorizontal: 2,
    paddingVertical: 2,
  },
  conversationSendRow: {
    marginTop: 10,
    alignItems: "flex-end",
  },
  resultActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginTop: 20,
  },
});

export default PresentSarasPanel;
